import { useState, useEffect } from 'react';
import type { LicenseTier, LicenseHistoryEntry } from '@/mocks/saLicensing';
import { getLicenseHistory, licenseTypeLabels } from '@/mocks/saLicensing';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface LicenseDetailModalProps {
  license: LicenseTier;
  onClose: () => void;
  onSave: (updated: LicenseTier) => void;
}

export default function LicenseDetailModal({ license, onClose, onSave }: LicenseDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'config' | 'history' | 'features' | 'flags'>('config');

  // Config state
  const [name, setName] = useState(license.name);
  const [description, setDescription] = useState(license.description);
  const [priceMonthly, setPriceMonthly] = useState(license.priceMonthly);
  const [priceAnnual, setPriceAnnual] = useState(license.priceAnnual);
  const [priceLifetime, setPriceLifetime] = useState(license.priceLifetime);
  const [trialDays, setTrialDays] = useState(license.trialDays);
  const [maxRadios, setMaxRadios] = useState(license.maxRadios);
  const [maxUsers, setMaxUsers] = useState(license.maxUsers);
  const [storageGB, setStorageGB] = useState(license.storageGB);
  const [bandwidthGB, setBandwidthGB] = useState(license.bandwidthGB);
  const [streamingBitrateKbps, setStreamingBitrateKbps] = useState(license.streamingBitrateKbps);

  // Feature toggles
  const [marketplace, setMarketplace] = useState(license.marketplace);
  const [tv, setTv] = useState(license.tv);
  const [apps, setApps] = useState(license.apps);
  const [ai, setAi] = useState(license.ai);
  const [advertising, setAdvertising] = useState(license.advertising);
  const [analytics, setAnalytics] = useState(license.analytics);
  const [streaming, setStreaming] = useState(license.streaming);
  const [api, setApi] = useState(license.api);
  const [customDomain, setCustomDomain] = useState(license.customDomain);
  const [verification, setVerification] = useState(license.verification);
  const [frequencyPreferred, setFrequencyPreferred] = useState(license.frequencyPreferred);

  // Feature flags
  const [flagsStr, setFlagsStr] = useState(license.featureFlags.join('\n'));

  const [active, setActive] = useState(license.active);
  const [visible, setVisible] = useState(license.visible);
  const [saving, setSaving] = useState(false);

  const history = getLicenseHistory(license.id);

  useEffect(() => {
    setName(license.name);
    setDescription(license.description);
    setPriceMonthly(license.priceMonthly);
    setPriceAnnual(license.priceAnnual);
    setPriceLifetime(license.priceLifetime);
    setTrialDays(license.trialDays);
    setMaxRadios(license.maxRadios);
    setMaxUsers(license.maxUsers);
    setStorageGB(license.storageGB);
    setBandwidthGB(license.bandwidthGB);
    setStreamingBitrateKbps(license.streamingBitrateKbps);
    setMarketplace(license.marketplace);
    setTv(license.tv);
    setApps(license.apps);
    setAi(license.ai);
    setAdvertising(license.advertising);
    setAnalytics(license.analytics);
    setStreaming(license.streaming);
    setApi(license.api);
    setCustomDomain(license.customDomain);
    setVerification(license.verification);
    setFrequencyPreferred(license.frequencyPreferred);
    setFlagsStr(license.featureFlags.join('\n'));
    setActive(license.active);
    setVisible(license.visible);
  }, [license]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      await SwalAlert.warning('Validación', 'El nombre de la licencia es requerido.');
      return;
    }

    setSaving(true);
    const parsedFlags = flagsStr
      .split('\n')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    const updated: LicenseTier = {
      ...license,
      name: trimmedName,
      description: description.trim(),
      priceMonthly,
      priceAnnual,
      priceLifetime,
      trialDays,
      maxRadios,
      maxUsers,
      storageGB,
      bandwidthGB,
      streamingBitrateKbps,
      marketplace,
      tv,
      apps,
      ai,
      advertising,
      analytics,
      streaming,
      api,
      customDomain,
      verification,
      frequencyPreferred,
      featureFlags: parsedFlags,
      active,
      visible,
      updatedAt: new Date().toISOString().split('T')[0],
    };

    onSave(updated);
    setSaving(false);
    await SwalAlert.toast('Licencia actualizada.', 'success');
  };

  const toggleFeature = (val: boolean, setter: (v: boolean) => void) => setter(!val);

  const handleRollback = async (entry: LicenseHistoryEntry) => {
    const result = await SwalAlert.confirm(
      '¿Revertir cambio?',
      `Vas a revertir "${entry.detail}". Esta acción no se puede deshacer.`,
      'warning',
    );
    if (result.isConfirmed) {
      await SwalAlert.toast('Rollback simulado.', 'info');
    }
  };

  const renderToggle = (label: string, value: boolean, setter: (v: boolean) => void) => (
    <button
      onClick={() => toggleFeature(value, setter)}
      className={`flex items-center gap-2 p-2 rounded-lg transition-all cursor-pointer text-left w-full ${
        value
          ? 'bg-emerald-50 border border-emerald-200'
          : 'bg-background-100 border border-background-200'
      }`}
    >
      <span className={`w-8 h-5 rounded-full transition-colors relative shrink-0 ${value ? 'bg-emerald-500' : 'bg-background-300'}`}>
        <span
          className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
          style={{ left: value ? '0.9375rem' : '0.125rem' }}
        ></span>
      </span>
      <span className={`text-[10px] font-medium flex-1 ${value ? 'text-emerald-700' : 'text-foreground-400'}`}>
        {label}
      </span>
      {value && <i className="ri-check-line text-emerald-500 text-xs"></i>}
    </button>
  );

  const tabs = [
    { key: 'config' as const, label: 'Configuración', icon: 'ri-settings-3-line' },
    { key: 'features' as const, label: 'Features', icon: 'ri-checkbox-multiple-line' },
    { key: 'flags' as const, label: 'Feature Flags', icon: 'ri-toggle-line' },
    { key: 'history' as const, label: 'Historial', icon: 'ri-history-line' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="shrink-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${license.color}14` }}>
              <i className="ri-key-2-line text-lg" style={{ color: license.color }}></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">{license.name}</h3>
              <p className="text-foreground-400 text-[10px]">{licenseTypeLabels[license.type]} · ID: {license.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="shrink-0 border-b border-background-200 px-5 flex items-center gap-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[10px] font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-foreground-950 text-foreground-950'
                  : 'border-transparent text-foreground-400 hover:text-foreground-600'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              {tab.key === 'history' && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[8px]">{history.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'config' && (
            <div className="space-y-6">
              {/* General */}
              <div className="space-y-3">
                <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Información General</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Nombre</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Tipo</label>
                    <div className="px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-500 font-medium">
                      {licenseTypeLabels[license.type]}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Descripción</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    maxLength={300}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none"
                  />
                  <p className="text-foreground-300 text-[9px] mt-0.5 text-right">{description.length}/300</p>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-3">
                <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Precios</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Precio Mensual (USD)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                      <input
                        type="number"
                        min={0}
                        value={priceMonthly}
                        onChange={(e) => setPriceMonthly(parseInt(e.target.value, 10) || 0)}
                        className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono font-bold text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Precio Anual (USD)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                      <input
                        type="number"
                        min={0}
                        value={priceAnnual}
                        onChange={(e) => setPriceAnnual(parseInt(e.target.value, 10) || 0)}
                        className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono font-bold text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Precio Lifetime (USD)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                      <input
                        type="number"
                        min={0}
                        value={priceLifetime}
                        onChange={(e) => setPriceLifetime(parseInt(e.target.value, 10) || 0)}
                        className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono font-bold text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Días de prueba</label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {[0, 3, 7, 14, 30, 60].map((d) => (
                      <button
                        key={d}
                        onClick={() => setTrialDays(d)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          trialDays === d
                            ? 'bg-foreground-950 text-background-50'
                            : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                        }`}
                      >
                        {d === 0 ? 'Sin prueba' : `${d} días`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Limits */}
              <div className="space-y-3">
                <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Límites y Capacidad</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Máx. Radios</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        value={maxRadios === null ? '' : maxRadios}
                        onChange={(e) => {
                          const v = e.target.value;
                          setMaxRadios(v === '' ? null : parseInt(v, 10));
                        }}
                        placeholder="Ilimitado"
                        className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Máx. Usuarios</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        value={maxUsers === null ? '' : maxUsers}
                        onChange={(e) => {
                          const v = e.target.value;
                          setMaxUsers(v === '' ? null : parseInt(v, 10));
                        }}
                        placeholder="Ilimitado"
                        className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Storage (GB)</label>
                    <input
                      type="number"
                      min={0}
                      value={storageGB}
                      onChange={(e) => setStorageGB(parseInt(e.target.value, 10) || 0)}
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Bandwidth (GB/mes)</label>
                    <input
                      type="number"
                      min={0}
                      value={bandwidthGB === null ? '' : bandwidthGB}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBandwidthGB(v === '' ? null : parseInt(v, 10));
                      }}
                      placeholder="Ilimitado"
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Streaming Bitrate (kbps)</label>
                    <input
                      type="number"
                      min={0}
                      value={streamingBitrateKbps === null ? '' : streamingBitrateKbps}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStreamingBitrateKbps(v === '' ? null : parseInt(v, 10));
                      }}
                      placeholder="Custom"
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-4">
              <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Features y Capacidades</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {renderToggle('Marketplace', marketplace, setMarketplace)}
                {renderToggle('TV Channel', tv, setTv)}
                {renderToggle('Apps Móviles', apps, setApps)}
                {renderToggle('Inteligencia Artificial', ai, setAi)}
                {renderToggle('Publicidad', advertising, setAdvertising)}
                {renderToggle('Analytics Avanzados', analytics, setAnalytics)}
                {renderToggle('Streaming', streaming, setStreaming)}
                {renderToggle('API de Acceso', api, setApi)}
                {renderToggle('Dominio Personalizado', customDomain, setCustomDomain)}
                {renderToggle('Verificación', verification, setVerification)}
                {renderToggle('Frecuencia Preferida', frequencyPreferred, setFrequencyPreferred)}
              </div>
            </div>
          )}

          {activeTab === 'flags' && (
            <div className="space-y-4">
              <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Feature Flags</h4>
              <p className="text-foreground-400 text-[10px]">Un flag por línea. Usá el formato ff_nombre_descripcion.</p>
              <textarea
                value={flagsStr}
                onChange={(e) => setFlagsStr(e.target.value)}
                rows={10}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-xl text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none"
                placeholder="ff_streaming_basic&#10;ff_analytics_pro"
              />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Historial de Cambios</h4>
              {history.length === 0 ? (
                <p className="text-foreground-400 text-xs">Sin historial disponible.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 p-3 rounded-xl bg-background-100 border border-background-200">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        entry.action === 'created' ? 'bg-emerald-50 text-emerald-600' :
                        entry.action === 'updated' ? 'bg-sky-50 text-sky-600' :
                        entry.action === 'duplicated' || entry.action === 'cloned' ? 'bg-violet-50 text-violet-600' :
                        entry.action === 'published' ? 'bg-accent-50 text-accent-600' :
                        entry.action === 'hidden' ? 'bg-amber-50 text-amber-600' :
                        'bg-background-200 text-foreground-500'
                      }`}>
                        <i className={`${
                          entry.action === 'created' ? 'ri-add-line' :
                          entry.action === 'updated' ? 'ri-edit-line' :
                          entry.action === 'duplicated' || entry.action === 'cloned' ? 'ri-git-branch-line' :
                          entry.action === 'published' ? 'ri-eye-line' :
                          entry.action === 'hidden' ? 'ri-eye-off-line' :
                          entry.action === 'activated' ? 'ri-play-circle-line' :
                          'ri-pause-circle-line'
                        } text-xs`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-foreground-800 text-xs font-semibold capitalize">
                            {entry.action === 'created' ? 'Creación' :
                             entry.action === 'updated' ? 'Actualización' :
                             entry.action === 'duplicated' ? 'Duplicado' :
                             entry.action === 'cloned' ? 'Clonado' :
                             entry.action === 'published' ? 'Publicado' :
                             entry.action === 'hidden' ? 'Ocultado' :
                             entry.action === 'activated' ? 'Activado' : 'Desactivado'}
                          </span>
                          <span className="text-foreground-300 text-[9px]">{entry.timestamp.split('T')[0]} {entry.timestamp.split('T')[1].substring(0, 5)}</span>
                        </div>
                        <p className="text-foreground-500 text-[10px] mt-0.5">{entry.detail}</p>
                        <p className="text-foreground-300 text-[9px] mt-0.5">{entry.user}</p>
                      </div>
                      <button
                        onClick={() => handleRollback(entry)}
                        className="shrink-0 px-2.5 py-1.5 rounded-lg bg-background-200 text-foreground-500 text-[9px] font-semibold hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-go-back-line text-[9px] mr-0.5"></i>
                        Rollback
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status toggles */}
        <div className="shrink-0 border-t border-background-200 px-5 py-3 flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <button
              onClick={() => setActive(!active)}
              className={`w-9 h-5 rounded-full transition-colors relative ${active ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: active ? '1.125rem' : '0.125rem' }}></span>
            </button>
            <span className="text-foreground-600 text-[10px] font-medium">{active ? 'Activa' : 'Inactiva'}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <button
              onClick={() => setVisible(!visible)}
              className={`w-9 h-5 rounded-full transition-colors relative ${visible ? 'bg-sky-500' : 'bg-background-300'}`}
            >
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: visible ? '1.125rem' : '0.125rem' }}></span>
            </button>
            <span className="text-foreground-600 text-[10px] font-medium">{visible ? 'Visible (público)' : 'Oculta'}</span>
          </label>
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-background-50 border-t border-background-200 p-4 flex items-center gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              name.trim() && !saving
                ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                : 'bg-background-200 text-foreground-300 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin text-xs"></i>
                Guardando...
              </span>
            ) : (
              <>
                <i className="ri-save-line text-xs mr-1"></i>
                Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}